import {
  BaseEdge,
  EdgeLabelRenderer,
  useReactFlow,
  type EdgeProps,
} from '@xyflow/react';
import type { ConnectionType, LinkEvidence, MonitorSource, NodeStatus } from '@ukrop-nm/shared';
import {
  DEFAULT_EDGE_COLOR,
  DIAGRAM_GRID_SIZE,
  STATUS_COLORS,
  displayPortId,
  isLogicalEthernetPort,
  isWifiPortId,
  snapCoord,
} from '@ukrop-nm/shared';
import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { ChannelTypeIcon } from './ChannelTypeIcon';
import {
  EDGE_DASH_ARRAY,
  buildAvoidPath,
  computeLabelNudgeY,
  computeLaneOffset,
  projectOntoPolyline,
} from '../edges/buildAvoidPath';
import { useEdgeRouting } from '../edges/EdgeRoutingContext';
import { usePortHover } from '../edges/PortHoverContext';
import { diagramStore } from '../state/diagramStore';
import { buildEdgeTooltipContent } from '../tooltips/buildInfoTooltipText';
import { useInfoTooltipHandlers } from '../tooltips/DiagramTooltipContext';

export type UkropEdgeData = {
  label?: string;
  status?: NodeStatus;
  evidence?: LinkEvidence;
  monitorSource?: MonitorSource;
  connectionType?: ConnectionType;
  color?: string;
  sourcePort?: string;
  targetPort?: string;
  labelT?: number;
  routeOffset?: number;
  editable?: boolean;
};

const TYPE_SHORT_UK: Record<ConnectionType, string> = {
  ethernet: 'Eth',
  fiber: 'Опто',
  wifi: 'Wi‑Fi',
  wan: 'WAN',
  sip: 'SIP',
  logical: 'Лог',
  unknown: '?',
};

const ROUTE_OFFSET_MAX = 180;

function edgeTouchesHoveredPort(
  nodeId: string,
  edgePort: string | undefined,
  hovered: { nodeId: string; portId: string },
): boolean {
  if (hovered.nodeId !== nodeId) return false;
  const port = displayPortId(edgePort);
  const hot = displayPortId(hovered.portId);
  if (port === hot) return true;
  // Legacy / unassigned ethernet: highlight when any ethernet-like port on the node is hovered
  if (isLogicalEthernetPort(port) && !isWifiPortId(hot)) return true;
  return false;
}

function clamp(n: number, lo: number, hi: number) {
  return Math.min(hi, Math.max(lo, n));
}

export function UkropEdge({
  id,
  source,
  target,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  selected,
}: EdgeProps) {
  const d = (data || {}) as UkropEdgeData;
  const { hovered } = usePortHover();
  const { screenToFlowPosition } = useReactFlow();
  const { obstacles, fast, peers } = useEdgeRouting();
  const sourcePort = d.sourcePort;
  const targetPort = d.targetPort;

  const [dragLabelT, setDragLabelT] = useState<number | null>(null);
  const [dragRouteOffset, setDragRouteOffset] = useState<number | null>(null);
  const [labelHot, setLabelHot] = useState(false);
  const dragging = useRef(false);
  const moved = useRef(false);
  const basePointsRef = useRef<{ x: number; y: number }[]>([]);
  const dragLiveRef = useRef({ t: 0.5, route: 0 });

  const labelT = dragLabelT ?? d.labelT;
  const routeOffset = dragRouteOffset ?? d.routeOffset ?? 0;

  useEffect(() => {
    if (dragging.current) return;
    setDragLabelT(null);
    setDragRouteOffset(null);
  }, [d.labelT, d.routeOffset]);

  const laneOffset = useMemo(
    () => computeLaneOffset(id, source, target, peers),
    [id, source, target, peers],
  );

  const labelNudgeY = useMemo(
    () => computeLabelNudgeY(id, source, target, peers),
    [id, source, target, peers],
  );

  const routed = useMemo(
    () =>
      buildAvoidPath({
        sourceX,
        sourceY,
        targetX,
        targetY,
        sourcePosition,
        targetPosition,
        obstacles,
        laneOffset,
        routeOffset,
        labelNudgeY,
        labelT,
        endpointIds: [source, target],
        fast,
      }),
    [
      sourceX,
      sourceY,
      targetX,
      targetY,
      sourcePosition,
      targetPosition,
      obstacles,
      laneOffset,
      routeOffset,
      labelNudgeY,
      labelT,
      source,
      target,
      fast,
    ],
  );

  const { path, labelX, labelY } = routed;

  const evidence = d.evidence ?? 'unknown';
  const custom = d.color?.trim();
  const stroke =
    custom ||
    (evidence === 'unknown'
      ? '#38bdf8'
      : d.status
        ? STATUS_COLORS[d.status]
        : DEFAULT_EDGE_COLOR);

  const portHot = Boolean(
    hovered &&
      (edgeTouchesHoveredPort(source, sourcePort, hovered) ||
        edgeTouchesHoveredPort(target, targetPort, hovered)),
  );
  const highlighted = portHot || labelHot || selected;
  const dash = evidence === 'measured' ? undefined : EDGE_DASH_ARRAY;

  const onLabelPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (!d.editable) return;
      e.preventDefault();
      e.stopPropagation();
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      dragging.current = true;
      moved.current = false;
      // Snapshot zero-offset path once for projection (avoid per-frame rebuild)
      basePointsRef.current = buildAvoidPath({
        sourceX,
        sourceY,
        targetX,
        targetY,
        sourcePosition,
        targetPosition,
        obstacles,
        laneOffset,
        routeOffset: 0,
        labelNudgeY,
        labelT: undefined,
        endpointIds: [source, target],
        fast: true,
      }).points;
      dragLiveRef.current = {
        t: typeof labelT === 'number' ? labelT : 0.5,
        route: routeOffset,
      };
    },
    [
      d.editable,
      labelT,
      routeOffset,
      sourceX,
      sourceY,
      targetX,
      targetY,
      sourcePosition,
      targetPosition,
      obstacles,
      laneOffset,
      labelNudgeY,
      source,
      target,
    ],
  );

  const onLabelPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (!dragging.current) return;
      moved.current = true;
      const flow = screenToFlowPosition({ x: e.clientX, y: e.clientY });
      const proj = projectOntoPolyline(basePointsRef.current, flow.x, flow.y);
      const nextT = clamp(proj.t, 0.08, 0.92);
      const nextRoute = clamp(
        snapCoord(proj.lateral, DIAGRAM_GRID_SIZE),
        -ROUTE_OFFSET_MAX,
        ROUTE_OFFSET_MAX,
      );
      dragLiveRef.current = { t: nextT, route: nextRoute };
      setDragLabelT(nextT);
      setDragRouteOffset(nextRoute);
    },
    [screenToFlowPosition],
  );

  const onLabelPointerUp = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (!dragging.current) return;
      dragging.current = false;
      try {
        (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      if (!moved.current) return;
      const { t, route } = dragLiveRef.current;
      diagramStore.updateEdges((list) =>
        list.map((ed) =>
          ed.id === id
            ? {
                ...ed,
                labelT: t,
                routeOffset: Math.abs(route) < 1 ? undefined : route,
              }
            : ed,
        ),
      );
    },
    [id],
  );

  const typeLabel = d.connectionType ? TYPE_SHORT_UK[d.connectionType] : '';
  const srcPort = displayPortId(sourcePort);
  const tgtPort = displayPortId(targetPort);
  const name =
    d.label?.trim() ||
    (srcPort || tgtPort ? `${srcPort} → ${tgtPort}` : '');

  const edgeTipHandlers = useInfoTooltipHandlers(() => {
    const edge = diagramStore.getSnapshot().edges.find((e) => e.id === id);
    return edge ? buildEdgeTooltipContent(edge) : null;
  });

  return (
    <>
      <BaseEdge
        id={id}
        path={path}
        className={highlighted ? 'ukrop-edge-path is-hot' : 'ukrop-edge-path'}
        style={{
          stroke,
          strokeWidth: highlighted ? 4.5 : 2,
          strokeDasharray: dash,
          opacity: highlighted ? 1 : selected ? 0.95 : 0.85,
          transition: 'stroke-width 0.12s ease, opacity 0.12s ease',
        }}
        interactionWidth={28}
      />
      <EdgeLabelRenderer>
        {highlighted ? (
          <svg
            className="ukrop-edge-hot-svg"
            aria-hidden
            style={{
              position: 'absolute',
              overflow: 'visible',
              left: 0,
              top: 0,
              pointerEvents: 'none',
              zIndex: 6,
            }}
          >
            <path
              d={path}
              fill="none"
              stroke={stroke}
              strokeWidth={6}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray={dash}
              opacity={0.35}
            />
            <path
              d={path}
              fill="none"
              stroke={stroke}
              strokeWidth={3.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray={dash}
              opacity={1}
            />
          </svg>
        ) : null}
        <div
          className={`ukrop-edge-badge nodrag nopan${d.editable ? ' is-editable' : ''}${
            selected ? ' is-selected' : ''
          }${highlighted ? ' is-hot' : ''}${dragging.current ? ' is-dragging' : ''}`}
          style={{
            position: 'absolute',
            width: 'max-content',
            maxWidth: '9.5rem',
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            pointerEvents: 'all',
            zIndex: 7,
            ['--ukrop-edge-accent' as string]: custom || stroke,
          }}
          onMouseEnter={(e) => {
            setLabelHot(true);
            edgeTipHandlers.onMouseEnter(e);
          }}
          onMouseLeave={() => {
            setLabelHot(false);
            edgeTipHandlers.onMouseLeave();
          }}
          onPointerDown={(e) => {
            edgeTipHandlers.onPointerDown();
            onLabelPointerDown(e);
          }}
          onPointerMove={onLabelPointerMove}
          onPointerUp={onLabelPointerUp}
          onPointerCancel={onLabelPointerUp}
        >
          {d.connectionType ? <ChannelTypeIcon type={d.connectionType} size={18} /> : null}
          <span className="ukrop-edge-badge-text">
            {typeLabel ? <span className="ukrop-edge-badge-type">{typeLabel}</span> : null}
            {name ? <span className="ukrop-edge-badge-name">{name}</span> : null}
          </span>
        </div>
      </EdgeLabelRenderer>
    </>
  );
}
